import { useAuth } from '@commander/auth';
import { logger } from '@commander/logger';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Editor,
  Input,
  Label,
  LexicalViewer,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetContent,
  Switch,
  toast,
} from '@commander/ui';
import { useUserDetailsData } from '@commander/user-details';
import { RiAttachment2, RiCloseLine, RiSendPlaneLine } from '@remixicon/react';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { isSystemVariable } from '../../constants/systemVariables';
import {
  useDeleteEmailAttachment,
  useEmailSignature,
  useEmailSignatures,
  useEmailTemplate,
  useEmailTemplates,
  useQuoteSigningLink,
  useRenderEmailTemplate,
  useUploadEmailAttachment,
} from '../../hooks';
import type {
  Attachment,
  Email,
  EmailSignature,
  EmailTemplate,
  QuoteComposerContext,
  StructuredRecipient,
} from '../../types';
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENT_LABEL } from '../../utils/attachments';
import { ensureSigningLink } from '../../utils/ensureSigningLink';
import { AiContentGenerator } from '../ai/AiContentGenerator';
import { ContactRecipientMultiSelect } from './ContactRecipientMultiSelect';

/**
 * Deduplicates recipients by email address, preferring entries with tenantContactId.
 */
function deduplicateRecipients(
  recipients: StructuredRecipient[],
  excludeEmail?: string
): StructuredRecipient[] {
  const recipientMap = new Map<string, StructuredRecipient>();
  const excludeLower = excludeEmail?.toLowerCase();

  for (const r of recipients) {
    const emailLower = r.address.toLowerCase();
    if (emailLower === excludeLower) continue;

    const existing = recipientMap.get(emailLower);
    if (!existing || (r.tenantContactId && !existing.tenantContactId)) {
      recipientMap.set(emailLower, r);
    }
  }
  return Array.from(recipientMap.values());
}

/**
 * Builds the recipient list for a reply-all, deduplicating and excluding the current user.
 */
function buildReplyAllRecipients(email: Email, currentUserEmail: string): StructuredRecipient[] {
  const allRecipients: StructuredRecipient[] = [];

  if (email.direction === 'outgoing') {
    if (email.to?.length) allRecipients.push(...email.to);
    if (email.cc?.length) allRecipients.push(...email.cc);
  } else {
    allRecipients.push({ address: email.from, name: undefined, tenantContactId: undefined });
    if (email.to?.length) allRecipients.push(...email.to);
    if (email.cc?.length) allRecipients.push(...email.cc);
  }

  return deduplicateRecipients(allRecipients, currentUserEmail);
}

interface MobileEmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emailData: any, draftId?: string) => Promise<void>;
  recipientEmail?: string;
  recipientName?: string;
  contactId?: string;
  replyToEmail?: Email;
  isReplyAll?: boolean;
  template?: EmailTemplate;
  draftEmail?: Email;
  structuredRecipients?: StructuredRecipient[];
  disableRecipientEditing?: boolean;
  masterCompanyLocationId?: string; // Location ID to scope contact search
  initialSubject?: string;
  initialAttachments?: Attachment[];
  initialBodyLexical?: any;
  quoteContext?: QuoteComposerContext; // Set when composing a quote send
}

/**
 * Full-screen mobile email composer
 * Optimized for touch interfaces with simplified layout
 */
export function MobileEmailComposer({
  isOpen,
  onClose,
  onSend,
  recipientEmail = '',
  recipientName = '',
  contactId,
  replyToEmail,
  isReplyAll = false,
  template,
  draftEmail,
  structuredRecipients,
  disableRecipientEditing = false,
  masterCompanyLocationId,
  initialSubject,
  initialAttachments,
  initialBodyLexical,
  quoteContext,
}: MobileEmailComposerProps) {
  // Recipient state
  const [toRecipients, setToRecipients] = useState<StructuredRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<StructuredRecipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<StructuredRecipient[]>([]);
  const [subject, setSubject] = useState('');
  const [bodySerializedState, setBodySerializedState] = useState<any>(null);
  const [editorKey, setEditorKey] = useState<string>('new');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(template || null);
  const [selectedSignature, setSelectedSignature] = useState<EmailSignature | null>(null);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [isReply, setIsReply] = useState(false);
  const [saveDraft, setSaveDraft] = useState(false);
  const [enrollInSequence, setEnrollInSequence] = useState(false);

  // Inline image attachment IDs
  const [inlineImageIds, setInlineImageIds] = useState<string[]>([]);

  const { getSigningUrl, handleSigningUrlResolved } = useQuoteSigningLink(quoteContext);

  // Template state
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [templateApplied, setTemplateApplied] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Template a loaded draft was composed from. Pre-selects the dropdown so the
  // draft shows which template produced it, while suppressing the fetch-and-apply
  // that a manual pick triggers — otherwise the draft's edited body would be
  // overwritten with the raw template.
  const draftTemplateIdRef = useRef<string | null>(null);

  // The recipient contact the applied body was personalized for. The template's
  // system variables render server-side from a single contact, so if the
  // recipient changes after apply the rendered body is stale for the new one.
  const appliedContactIdRef = useRef<string | null>(null);

  // AI email writer state
  const [isAiPopoverOpen, setIsAiPopoverOpen] = useState(false);

  // Get current user's email
  const { user } = useAuth();

  // Standing autopilot settings (admin-managed): show the toggle locked on when sequencing
  // is already handled automatically — first-send (auto-enroll) or classic autopilot (its own
  // trigger sequences the prospect, so per-send enrollment is disabled to avoid conflict).
  const userDetails = useUserDetailsData();
  const firstSendAlwaysOn = userDetails?.autopilot_first_send_enabled === true;
  const classicAutopilotOn = userDetails?.auto_pilot_enabled === true;
  const sequenceLockedOn = firstSendAlwaysOn || classicAutopilotOn;

  // Fetch templates list from API
  const { data: templatesData } = useEmailTemplates({ page: 1, limit: 100 });
  const templates = templatesData?.templates ?? [];

  // Fetch full template details when selected
  const { data: fullTemplateData } = useEmailTemplate(selectedTemplateId || '', {
    enabled: !!selectedTemplateId && selectedTemplateId !== draftTemplateIdRef.current,
  });

  // Server-side render on apply (resolves system variables from the recipient).
  const renderTemplateMutation = useRenderEmailTemplate();

  // Fetch signatures list from API
  const { data: signaturesData } = useEmailSignatures({ page: 1, limit: 100 });
  const signatures = signaturesData?.signatures ?? [];

  // Fetch full signature details when selected
  const { data: fullSignatureData } = useEmailSignature(selectedSignatureId || '', {
    enabled: !!selectedSignatureId,
  });

  // Attachment mutations
  const uploadAttachmentMutation = useUploadEmailAttachment();
  const deleteAttachmentMutation = useDeleteEmailAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await uploadAttachmentMutation.mutateAsync({ file, is_inline: true });
      if (!response || !response.id || !response.file_url) {
        throw new Error('Invalid response from server');
      }
      setInlineImageIds((prev) => [...prev, response.id]);
      return response.file_url;
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
      logger.error('Image upload failed:', error);
      throw error;
    }
  };

  // Preselect default signature when opened
  useEffect(() => {
    if (isOpen && signatures.length > 0) {
      const defaultSignature = signatures.find((sig) => sig.isDefault);
      if (defaultSignature) {
        setSelectedSignatureId(defaultSignature.id);
      }
    }
  }, [isOpen, signatures]);

  // Initialize recipients
  useEffect(() => {
    if (structuredRecipients && structuredRecipients.length > 0) {
      setToRecipients(structuredRecipients);
    } else if (recipientEmail) {
      setToRecipients([
        {
          address: recipientEmail,
          name: recipientName || undefined,
          tenantContactId: contactId,
        },
      ]);
    }
  }, [recipientEmail, recipientName, contactId, structuredRecipients]);

  // Pre-populate subject from prefill
  useEffect(() => {
    if (initialSubject) {
      setSubject(initialSubject);
    }
  }, [initialSubject]);

  // Pre-populate attachments from prefill
  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0) {
      setAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  // Pre-populate body from prefill (Lexical JSON)
  useEffect(() => {
    if (initialBodyLexical) {
      setBodySerializedState(initialBodyLexical);
      setEditorKey(`prefill-${Date.now()}`);
    }
  }, [initialBodyLexical]);

  // Pre-populate form when editing a draft
  useEffect(() => {
    if (draftEmail) {
      setIsDraftMode(true);
      setToRecipients(draftEmail.to);
      setCcRecipients(draftEmail.cc || []);
      setBccRecipients(draftEmail.bcc || []);
      setSubject(draftEmail.subject || '');
      setBodySerializedState(draftEmail.lexicalBody);
      setAttachments(draftEmail.metadata?.attachments || []);

      if (draftEmail.inReplyTo) {
        setIsReply(true);
      }

      // Pre-select the template the draft was composed from so the dropdown
      // reflects it. The ref suppresses the fetch-and-apply a manual pick would
      // trigger, preserving the draft's existing body.
      setSelectedTemplate(null);
      setVariableValues({});
      if (draftEmail.templateId) {
        draftTemplateIdRef.current = draftEmail.templateId;
        setSelectedTemplateId(draftEmail.templateId);
        setTemplateApplied(true);
      } else {
        draftTemplateIdRef.current = null;
        setSelectedTemplateId(null);
        setTemplateApplied(false);
      }

      if (draftEmail.cc && draftEmail.cc.length > 0) setShowCc(true);
      if (draftEmail.bcc && draftEmail.bcc.length > 0) setShowBcc(true);

      setEditorKey(`draft-${draftEmail.id}-${Date.now()}`);
    } else {
      setIsDraftMode(false);
      setIsReply(false);
    }
  }, [draftEmail]);

  // Handle template prop
  useEffect(() => {
    if (template) {
      if (!template.body || !template.variables) {
        setSelectedTemplateId(template.id);
      } else {
        setSelectedTemplate(template);
        setSubject(template.subject);
        setTemplateApplied(false);
        setBodySerializedState(template.body);
        setEditorKey(template.id);

        const initialValues: Record<string, string> = {};
        for (const v of template.variables) {
          initialValues[v.key] = '';
        }
        setVariableValues(initialValues);
      }
    }
  }, [template]);

  // Handle reply setup
  useEffect(() => {
    if (!replyToEmail) return;

    setSubject(replyToEmail.subject || '');
    setIsReply(true);

    if (isReplyAll && user?.email) {
      setToRecipients(buildReplyAllRecipients(replyToEmail, user.email));
    } else if (replyToEmail.direction === 'outgoing') {
      if (replyToEmail.to?.length) {
        setToRecipients([replyToEmail.to[0]]);
      }
    } else {
      setToRecipients([
        { address: replyToEmail.from, name: undefined, tenantContactId: undefined },
      ]);
    }
  }, [replyToEmail, isReplyAll, user?.email]);

  // Update template when full data loads
  useEffect(() => {
    if (fullTemplateData) {
      setSelectedTemplate(fullTemplateData);
      setSubject(fullTemplateData.subject);
      setBodySerializedState(fullTemplateData.body);
      setEditorKey(`${fullTemplateData.id}-${Date.now()}`);

      const initialValues: Record<string, string> = {};
      if (fullTemplateData.variables && fullTemplateData.variables.length > 0) {
        for (const v of fullTemplateData.variables) {
          initialValues[v.key] = '';
        }
      }
      setVariableValues(initialValues);
    }
  }, [fullTemplateData]);

  // Update signature when full data loads
  useEffect(() => {
    if (fullSignatureData) {
      setSelectedSignature(fullSignatureData);
    }
  }, [fullSignatureData]);

  // Re-arm the "Apply Template" step when the personalization recipient changes
  // after apply: the applied body was rendered for a specific contact, so a new
  // recipient would otherwise receive the previous one's name/company. Only the
  // rendered body is invalidated (the card re-appears); manual edits are kept.
  useEffect(() => {
    if (!templateApplied || !selectedTemplate) return;
    const currentContactId = toRecipients.find((r) => r.tenantContactId)?.tenantContactId ?? null;
    if (currentContactId !== appliedContactIdRef.current) {
      setTemplateApplied(false);
    }
  }, [toRecipients, templateApplied, selectedTemplate]);

  const handleTemplateSelect = (templateId: string) => {
    // A manual pick always fetches and applies, even when it matches the draft's
    // original template.
    draftTemplateIdRef.current = null;
    appliedContactIdRef.current = null;
    if (templateId === 'none') {
      setSelectedTemplate(null);
      setSelectedTemplateId(null);
      setSubject('');
      setBodySerializedState(null);
      setEditorKey('new');
      setVariableValues({});
      setTemplateApplied(false);
    } else {
      setSelectedTemplateId(templateId);
      setTemplateApplied(false);
    }
  };

  const handleAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`File too large. Max attachment size is ${MAX_ATTACHMENT_LABEL}.`);
      event.target.value = '';
      return;
    }

    try {
      const response = await uploadAttachmentMutation.mutateAsync({ file });
      const newAttachment: Attachment = {
        id: response.id,
        name: response.file_name,
        size: file.size,
        type: response.file_type,
        url: response.file_url,
        uploadedAt: new Date(response.created_at),
      };
      setAttachments([...attachments, newAttachment]);
      toast.success(`Uploaded ${file.name}`);
      event.target.value = '';
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload file');
      logger.error('Failed to upload attachment', error);
    }
  };

  const removeAttachment = async (id: string) => {
    try {
      await deleteAttachmentMutation.mutateAsync(id);
      setAttachments(attachments.filter((a) => a.id !== id));
      toast.success('Attachment removed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete attachment');
      logger.error('Failed to delete attachment', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Mirrors the desktop composer: offer follow-up enrollment for a single known
  // contact on a new (non-reply) email. The backend is the real gate — it only
  // enrolls when that contact resolves to an assigned prospect.
  const canEnrollInSequence =
    toRecipients.length === 1 && Boolean(toRecipients[0]?.tenantContactId) && !isReply;

  const buildEmailData = (isDraft: boolean) => {
    return {
      to: toRecipients,
      cc: ccRecipients.length > 0 ? ccRecipients : undefined,
      bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
      subject,
      body: bodySerializedState,
      attachmentIds: [...attachments.map((a) => a.id), ...inlineImageIds],
      // Fall back to selectedTemplateId so a draft opened for editing keeps its
      // template even when the full template was never fetched (display-only).
      templateId: selectedTemplate?.id ?? selectedTemplateId ?? undefined,
      signatureId: selectedSignature?.id,
      trackOpens: true,
      enrollInSequence: !isDraft && canEnrollInSequence ? enrollInSequence : undefined,
      replyToEmailId: replyToEmail?.id,
      isDraft,
    };
  };

  const handleSend = async () => {
    const emailData = buildEmailData(false);
    try {
      setIsSending(true);
      await onSend(emailData, isDraftMode ? draftEmail?.id : undefined);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send email');
      logger.error('Failed to send email', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = async () => {
    const draftData = buildEmailData(true);
    try {
      setIsSending(true);
      await onSend(draftData, isDraftMode ? draftEmail?.id : undefined);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save draft');
      logger.error('Failed to save draft', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setToRecipients([]);
    setCcRecipients([]);
    setBccRecipients([]);
    setSubject('');
    setBodySerializedState(null);
    setEditorKey('new');
    setAttachments([]);
    setInlineImageIds([]);
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
    draftTemplateIdRef.current = null;
    appliedContactIdRef.current = null;
    setSelectedSignature(null);
    setSelectedSignatureId(null);
    setVariableValues({});
    setTemplateApplied(false);
    setShowCc(false);
    setShowBcc(false);
    setIsReply(false);
    setSaveDraft(false);
    setEnrollInSequence(false);
    onClose();
  };

  // Custom (non-system) variables are the only ones collected by hand.
  const customVariables = (selectedTemplate?.variables ?? []).filter(
    (v) => !isSystemVariable(v.key)
  );

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    const tenantContactId = toRecipients.find((r) => r.tenantContactId)?.tenantContactId ?? null;

    try {
      const { subject: renderedSubject, body: renderedBody } =
        await renderTemplateMutation.mutateAsync({
          templateId: selectedTemplate.id,
          tenantContactId,
          variables: variableValues,
        });

      setSubject(renderedSubject);
      setBodySerializedState(ensureSigningLink(renderedBody, getSigningUrl()));
      setEditorKey(`applied-${Date.now()}`);
      appliedContactIdRef.current = tenantContactId;
      setTemplateApplied(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to apply template');
      logger.error('Failed to apply template', error);
    }
  };

  const handleAiContentGenerated = ({
    subject: generatedSubject,
    body,
  }: {
    subject?: string;
    body: any;
  }) => {
    setBodySerializedState(ensureSigningLink(body, getSigningUrl()));
    setEditorKey(`ai-generated-${Date.now()}`);
    if (generatedSubject) {
      setSubject(generatedSubject);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="bottom"
        className="p-0 flex flex-col rounded-none gap-0 [&>button]:hidden overflow-hidden"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: '100dvh',
          maxHeight: '100dvh',
          width: '100vw',
          maxWidth: '100vw',
        }}
      >
        {/* Header - with safe area padding for notch */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-background pt-[max(0.75rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <RiCloseLine className="h-5 w-5" />
            </Button>
            <span className="text-lg font-semibold">New Email</span>
          </div>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={
              toRecipients.length === 0 ||
              !subject ||
              isSending ||
              (!!selectedTemplate && !templateApplied)
            }
          >
            {isSending ? 'Sending...' : 'Send'}
          </Button>
        </div>

        {/* Scrollable content */}
        <ScrollArea className="flex-1 min-h-0 w-full">
          <div
            className="px-4 py-4 space-y-4"
            style={{ width: 'calc(100vw - 32px)', maxWidth: 'calc(100vw - 32px)' }}
          >
            {/* To field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">To:</Label>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowCc(!showCc)}
                  >
                    Cc
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowBcc(!showBcc)}
                  >
                    Bcc
                  </Button>
                </div>
              </div>
              <ContactRecipientMultiSelect
                value={toRecipients}
                onChange={setToRecipients}
                placeholder="Search contacts or enter email..."
                disabled={disableRecipientEditing}
                masterCompanyLocationId={masterCompanyLocationId}
              />
            </div>

            {/* Cc field */}
            {showCc && (
              <div className="space-y-2">
                <Label className="text-sm">Cc:</Label>
                <ContactRecipientMultiSelect
                  value={ccRecipients}
                  onChange={setCcRecipients}
                  placeholder="Search contacts or enter email..."
                  masterCompanyLocationId={masterCompanyLocationId}
                />
              </div>
            )}

            {/* Bcc field */}
            {showBcc && (
              <div className="space-y-2">
                <Label className="text-sm">Bcc:</Label>
                <ContactRecipientMultiSelect
                  value={bccRecipients}
                  onChange={setBccRecipients}
                  placeholder="Search contacts or enter email..."
                  masterCompanyLocationId={masterCompanyLocationId}
                />
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-sm">Subject:</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                disabled={isReply}
              />
            </div>

            {/* Template selector */}
            <div className="space-y-2">
              <Label className="text-sm">Template:</Label>
              <Select value={selectedTemplateId || 'none'} onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template variables form. System variables fill from the recipient;
                only custom variables are collected here. */}
            {selectedTemplate && !templateApplied && (
              <Card className="bg-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {customVariables.length > 0 ? 'Template Variables' : 'Apply Template'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {customVariables.length > 0
                      ? 'Fill in the variables below and click "Apply Template". Name, company, title and email fill in automatically from the recipient.'
                      : 'Name, company, title and email fill in automatically from the recipient. Click "Apply Template" to insert it.'}
                  </p>
                  {customVariables.map((variable) => (
                    <div key={variable.key} className="space-y-1">
                      <Label className="text-xs">{variable.label}</Label>
                      <Input
                        value={variableValues[variable.key] || ''}
                        onChange={(e) =>
                          setVariableValues({
                            ...variableValues,
                            [variable.key]: e.target.value,
                          })
                        }
                        placeholder={`Enter ${variable.label.toLowerCase()}`}
                      />
                    </div>
                  ))}
                  <Button
                    onClick={handleApplyTemplate}
                    className="w-full"
                    size="sm"
                    disabled={renderTemplateMutation.isPending}
                  >
                    {renderTemplateMutation.isPending ? 'Applying…' : 'Apply Template'}
                  </Button>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Email body editor */}
            <div className="min-h-[200px] relative w-full overflow-hidden">
              {!selectedTemplateId || bodySerializedState !== null ? (
                <Editor
                  key={editorKey}
                  editorSerializedState={bodySerializedState || undefined}
                  onSerializedChange={setBodySerializedState}
                  onImageUpload={handleImageUpload}
                  height="min-h-[200px]"
                />
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-sm text-muted-foreground">Loading template...</p>
                </div>
              )}
            </div>

            {/* AI Write Button */}
            <AiContentGenerator
              type="email"
              isOpen={isAiPopoverOpen}
              onOpenChange={setIsAiPopoverOpen}
              onContentGenerated={handleAiContentGenerated}
              buttonClassName="w-full"
              contactId={contactId}
              recipientName={recipientName}
              masterCompanyLocationId={masterCompanyLocationId}
              quoteId={quoteContext?.quoteId}
              onSigningUrlResolved={handleSigningUrlResolved}
            />

            {/* Add Attachment */}
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleAttachment}
                className="hidden"
                accept="*/*"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <RiAttachment2 className="h-4 w-4 mr-2" />
                Add Attachment
              </Button>
            </div>

            {/* Attachments list */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Attachments:</Label>
                <div className="space-y-1">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-2 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <RiAttachment2 className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm truncate">{attachment.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatFileSize(attachment.size)})
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        onClick={() => removeAttachment(attachment.id)}
                      >
                        <RiCloseLine className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Signature preview */}
            {selectedSignature && (selectedSignature.lexicalBody || selectedSignature.htmlBody) && (
              <div className="p-3 bg-muted rounded-md">
                <Label className="text-xs text-muted-foreground mb-2 block">Signature:</Label>
                {selectedSignature.contentType === 'html' && selectedSignature.htmlBody ? (
                  <div
                    className="email-html-content"
                    style={{
                      fontSize: '13px',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.5',
                    }}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedSignature.htmlBody),
                    }}
                  />
                ) : selectedSignature.lexicalBody ? (
                  <LexicalViewer
                    key={selectedSignature.id}
                    serializedState={selectedSignature.lexicalBody}
                  />
                ) : null}
              </div>
            )}

            <Separator />

            {/* Save Draft toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm">Save Draft</Label>
              <Switch checked={saveDraft} onCheckedChange={setSaveDraft} />
            </div>

            {/* Follow-up sequence toggle (parity with desktop composer) */}
            {canEnrollInSequence && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <Label className="text-sm">Send automatic follow-ups</Label>
                  <span className="text-xs text-muted-foreground">
                    {firstSendAlwaysOn
                      ? 'On for your account — first email to a prospect starts a follow-up sequence'
                      : classicAutopilotOn
                        ? 'Managed by Autopilot'
                        : 'First email to a prospect only — sends on days 7, 14 & 21, stops if they reply'}
                  </span>
                </div>
                <Switch
                  checked={sequenceLockedOn || enrollInSequence}
                  onCheckedChange={setEnrollInSequence}
                  disabled={sequenceLockedOn}
                />
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer - with safe area padding for home indicator */}
        <div className="px-4 py-3 border-t bg-background flex items-center gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button variant="outline" onClick={handleClose} disabled={isSending} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={saveDraft ? handleSaveDraft : handleSend}
            disabled={
              toRecipients.length === 0 ||
              !subject ||
              isSending ||
              (!saveDraft && !!selectedTemplate && !templateApplied)
            }
            className="flex-1"
          >
            <RiSendPlaneLine className="h-4 w-4 mr-2" />
            {isSending ? 'Sending...' : saveDraft ? 'Save Draft' : 'Send'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
